import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// Global flag to ensure we only process the initial launch URL once per app session.
// This prevents the "infinite refresh" loop where the app restarts and sees the same deep link again.
let hasConsumedInitialDeepLink = false;

/**
 * Simplified Deep Link Handler
 *
 * 1. Listens for deep links (hellobello:// or https://)
 * 2. Extracts 'sessionToken' query parameter
 * 3. Navigates to /auth/verify to let the existing auth flow handle the rest
 */
export const useDeepLink = () => {
  const navigate = useNavigate();
  const processingRef = useRef<string | null>(null);

  const handleDeepLink = (url: string) => {
    try {
      // Simple debounce to prevent double-firing/loops
      if (processingRef.current === url) {
        return;
      }
      processingRef.current = url;

      const urlObj = new URL(url);
      const sessionToken = urlObj.searchParams.get('sessionToken');

      if (sessionToken) {
        // Navigate to the verification page which handles the actual login logic
        navigate(`/auth/verify?sessionToken=${sessionToken}`);
      } else {
        console.warn('Deep link received but no sessionToken found');
      }

      // Reset debounce after 2s
      setTimeout(() => {
        processingRef.current = null;
      }, 2000);
    } catch (error) {
      console.error('Failed to parse deep link:', error);
    }
  };

  useEffect(() => {
    // Guard: Only run in Tauri
    if (typeof window === 'undefined' || !(window as any).__TAURI_INTERNALS__) return;

    let unlisten: (() => void) | undefined;

    const initDeepLinks = async () => {
      try {
        const { getCurrent, onOpenUrl } = await import('@tauri-apps/plugin-deep-link');

        // 1. Check launch URL - ONLY IF NOT YET CONSUMED
        // This is critical to avoid infinite auth loops on reload
        if (!hasConsumedInitialDeepLink) {
          const startUrls = await getCurrent();
          if (startUrls?.[0]) {
            handleDeepLink(startUrls[0]);
            hasConsumedInitialDeepLink = true;
          }
        }

        // 2. Listen for runtime URLs (new clicks while app is open)
        unlisten = await onOpenUrl((urls) => {
          if (urls?.[0]) {
            handleDeepLink(urls[0]);
          }
        });
      } catch (error) {
        console.error('Deep Link Plugin Error:', error);
      }
    };

    // Not awaited on purpose: an effect can't be async, and initDeepLinks
    // catches and logs its own errors.
    void initDeepLinks();

    return () => {
      if (unlisten) unlisten();
    };
  }, []); // Run once on mount

  return {};
};
